import * as Sentry from '@sentry/vue';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { attachNormalized, normalizeError } from './normalizeError';
import { redactUrl } from './redact';
import { ErrorKind, ErrorSource, type NormalizedError } from './errorTypes';
import { pushTrail } from './trail';

/**
 * The HTTP seam.
 *
 * `normalizeError` is the universal seam — it sees every error source. The interceptor owns only what
 * must happen once per response and must happen *even when nobody catches*:
 *
 *  1. 401 session-expiry -> logout. This used to live inside `LoadingStateModel.setError`, so the
 *     store calls without a try/catch never triggered it: an expired session on app load produced an
 *     unhandled rejection and a spinner that never stopped. Here it covers all 24 services at once.
 *  2. Trace correlation, captured at throw time while the context still exists.
 *  3. The trail entry, so a report can show what led up to the failure.
 *  4. Telling `onHttpRefusal`'s listeners about a 403 or 404, and keeping whether one claimed it.
 *
 * It **decorates, never replaces**: the original `AxiosError` is re-rejected, because ~20 call sites
 * do `error instanceof AxiosError`. `normalizeError` reads the attached classification back, so the
 * work happens once even though it is called twice.
 */

export interface HttpErrorHooks {
  onSessionExpired: () => void;
}

/**
 * The hooks the installed interceptor runs with, kept so a request that deliberately bypasses
 * `apiClient` can reach them through `reportBypassedHttpError`.
 *
 * Module-level rather than passed around, because the bypassing call sites are services that have
 * no way to reach `main.ts`'s hooks and no business knowing what they are. `undefined` until
 * `installHttpErrorInterceptor` runs, which is before any service can fire a request.
 */
let installedHooks: HttpErrorHooks | undefined;

/** A request the API answered 403 or 404, by its URL as the service sent it (`study/<id>/people`). */
export interface HttpRefusal {
  status: 403 | 404;
  url: string;
  /** When the request was sent, by `Date.now()`; missing for one that bypassed this interceptor's stamp. */
  sentAt?: number;
}

/**
 * A listener may be async. It resolves `true` to claim the refusal: it tells the reader about it
 * itself, so the toast or error screen the failed call would put up is dropped (`refusalClaim`).
 * Anything else leaves the refusal to the caller.
 */
type RefusalListener = (refusal: HttpRefusal) => unknown;

const refusalListeners = new Set<RefusalListener>();

/** Each refused request's error, to whether a listener claimed it. Weak, so an error is never kept. */
const refusalClaims = new WeakMap<object, Promise<boolean>>();

/**
 * How long a refused call's toast or error screen waits on its listeners. The lost-study check reads
 * the study list, and the client sets no timeout, so a read that stalls would otherwise hold a
 * refused write's toast for good.
 */
const CLAIM_WAIT_MS = 10_000;

const isObject = (value: unknown): value is object => value != null && typeof value === 'object';

const listenerFailed = () => {
  /* eslint-disable-next-line no-console */
  console.error('[errors] a refusal listener failed');
};

/**
 * Runs one listener, so its failure, now or later, is neither the request's nor the next listener's.
 * Resolves whether it claimed the refusal; a failed listener claimed nothing.
 */
const tellListener = (listener: RefusalListener, refusal: HttpRefusal): Promise<boolean> => {
  try {
    return Promise.resolve(listener(refusal)).then(
      claimed => claimed === true,
      () => {
        listenerFailed();
        return false;
      }
    );
  } catch {
    listenerFailed();
    return Promise.resolve(false);
  }
};

/** Whether any of the answers claimed the refusal, or `false` once `CLAIM_WAIT_MS` passes without all of them. */
const claimedInTime = (answers: Promise<boolean>[]): Promise<boolean> => new Promise(resolve => {
  const timer = setTimeout(() => resolve(false), CLAIM_WAIT_MS);
  // `tellListener` never rejects.
  Promise.all(answers).then(claims => {
    clearTimeout(timer);
    resolve(claims.includes(true));
  });
});

/**
 * Whether a listener claimed this refused request's error, once every listener has answered or
 * `CLAIM_WAIT_MS` has passed; nothing for an error no listener heard. The error store holds that
 * error's toast or error screen until then.
 */
export const refusalClaim = (error: unknown): Promise<boolean> | undefined =>
  isObject(error) ? refusalClaims.get(error) : undefined;

/**
 * Hears every 403 and 404 the shared client gets back, for as long as the returned function is not
 * called. The lost-study redirect listens while a study is open (users decision 107): every service
 * reaches the API through here, so no store has to report its own refusals.
 */
export const onHttpRefusal = (listener: RefusalListener): (() => void) => {
  refusalListeners.add(listener);
  return () => {
    refusalListeners.delete(listener);
  };
};

const START_KEY = '__appStartedAt';

type TimedConfig = InternalAxiosRequestConfig & { [START_KEY]?: number };

const durationOf = (config?: TimedConfig): number | undefined => {
  const startedAt = config?.[START_KEY];
  return typeof startedAt === 'number' ? Date.now() - startedAt : undefined;
};

const labelFor = (normalized: NormalizedError): string =>
  `${normalized.request?.method ?? 'GET'} ${normalized.request?.path ?? '?'}`;

/**
 * The three jobs the interceptor owns, for one failed response.
 *
 * Extracted so `reportBypassedHttpError` runs exactly this and cannot drift from it. It never
 * throws: bookkeeping must not turn one failure into two, and the caller still rejects or throws
 * the original error afterwards.
 */
const decorateHttpError = (error: unknown, hooks: HttpErrorHooks): void => {
  try {
    const normalized = normalizeError(error, { source: ErrorSource.HTTP });

    if (normalized.request) {
      normalized.request.durationMs = durationOf(
        (error as { config?: TimedConfig }).config
      );
      normalized.request.sentryTrace = Sentry.getTraceData()['sentry-trace'];
      // NOTE: only present if the backend sets it. Confirm with spdata-api before relying on it.
      const headers = (error as { response?: { headers?: Record<string, string> } }).response?.headers;
      normalized.request.requestId = headers?.['x-request-id'];
    }

    pushTrail({
      type: 'request',
      label: labelFor(normalized),
      status: normalized.httpStatus,
      ms: normalized.request?.durationMs
    });

    if (isObject(error)) {
      attachNormalized(error, normalized);
    }

    if (normalized.kind === ErrorKind.AUTH_EXPIRED) {
      hooks.onSessionExpired();
    }

    const status = normalized.httpStatus;
    const config = (error as { config?: TimedConfig }).config;
    const url = config?.url;
    if ((status === 403 || status === 404) && url && refusalListeners.size > 0) {
      const refusal: HttpRefusal = { status, url, sentAt: config?.[START_KEY] };
      const answers = [...refusalListeners].map(listener => tellListener(listener, refusal));
      if (isObject(error)) {
        refusalClaims.set(error, claimedInTime(answers));
      }
    }
  } catch {
    /* eslint-disable-next-line no-console */
    console.error('[errors] response interceptor failed');
  }
};

/**
 * Run the interceptor's handling for a failure that never went through `apiClient`.
 *
 * For the handful of requests that bypass it on purpose — today `streamPerfusionDashboard`, which
 * needs `fetch` because axios buffers the body it reads as a stream. Without this, an expired
 * session on such a request reached nobody: no logout, no trail entry, and a normalization the
 * error store would redo from scratch.
 *
 * Pass an `AxiosError`. `normalizeError` reads status, path and body off that shape, so anything
 * else classifies as `UNKNOWN` and a 401 goes unrecognised — which is the bug this exists to fix.
 *
 * A no-op before the interceptor is installed, which is the same window in which nothing can have
 * fired a request yet.
 */
export const reportBypassedHttpError = (error: unknown): void => {
  if (!installedHooks) {
    return;
  }
  decorateHttpError(error, installedHooks);
};

export const installHttpErrorInterceptor = (client: AxiosInstance, hooks: HttpErrorHooks): void => {
  installedHooks = hooks;

  client.interceptors.request.use(config => {
    (config as TimedConfig)[START_KEY] = Date.now();
    return config;
  });

  client.interceptors.response.use(
    response => {
      try {
        pushTrail({
          type: 'request',
          label: `${response.config?.method?.toUpperCase() ?? 'GET'} ${redactUrl(response.config?.url)?.path ?? '?'}`,
          status: response.status,
          ms: durationOf(response.config as TimedConfig)
        });
      } catch {
        // Never let bookkeeping turn a successful response into a failure.
      }
      return response;
    },
    (error: unknown) => {
      decorateHttpError(error, hooks);

      // Unchanged, so `instanceof AxiosError` keeps working at every existing call site.
      return Promise.reject(error);
    }
  );
};
